import React from 'react';
import Card from '../shared/Card';

const Schedule = () => {
  // Helper function to parse and render comments with links
  const renderComment = (comment) => {
    if (!comment) return null;
    
    // Check if the comment is just a link without any surrounding text
    if (comment.startsWith('{link:') && comment.endsWith('}')) {
      const linkContent = comment.slice(6, -1); // Remove {link: and }
      const [text, url] = linkContent.split(', url:').map(s => s.trim());
      
      // Check if this is an internal anchor link (starts with #)
      const isInternalLink = url.startsWith('#');
      
      return (
        <a 
          href={url}
          className="text-primary-purple hover:text-primary-orange underline"
          target={isInternalLink ? undefined : "_blank"}
          rel={isInternalLink ? undefined : "noopener noreferrer"}
        >
          {text}
        </a>
      );
    }
  
    // Process bullet points
    if (comment.includes('\n- ') || comment.startsWith('- ')) {
      const lines = comment.split('\n');
      let currentContent = [];
      let bulletPoints = [];
      let inList = false;
      
      lines.forEach((line, index) => {
        if (line.startsWith('- ')) {
          // Start bullet point list if not already in one
          if (!inList) {
            // Add any accumulated content before starting list
            if (currentContent.length > 0) {
              bulletPoints.push(
                <p key={`p-${index}`} className="mb-4">
                  {renderCommentParts(currentContent.join('\n'))}
                </p>
              );
              currentContent = [];
            }
            inList = true;
          }
          
          // Add bullet point
          bulletPoints.push(
            <li key={`li-${index}`} className="mb-2">
              {renderCommentParts(line.substring(2))}
            </li>
          );
        } else {
          // End list if we were in one
          if (inList) {
            bulletPoints.push(
              <ul key={`ul-${index}`} className="list-disc pl-6 mb-4">
                {bulletPoints.filter(item => item.type === 'li')}
              </ul>
            );
            // Reset bullet points, keeping only non-li elements
            bulletPoints = bulletPoints.filter(item => item.type !== 'li');
            inList = false;
          }
          
          // Add to current content
          currentContent.push(line);
        }
      });
      
      // If we're still in a list at the end, close it
      if (inList) {
        bulletPoints.push(
          <ul key="ul-end" className="list-disc pl-6 mb-4">
            {bulletPoints.filter(item => item.type === 'li')}
          </ul>
        );
        // Reset bullet points, keeping only non-li elements
        bulletPoints = bulletPoints.filter(item => item.type !== 'li');
      }
      
      // Add any remaining content
      if (currentContent.length > 0) {
        bulletPoints.push(
          <p key="p-end" className="mb-4">
            {renderCommentParts(currentContent.join('\n'))}
          </p>
        );
      }
      
      return <>{bulletPoints}</>;
    }
  
    // If just normal text with possible links and line breaks
    return renderCommentParts(comment);
  };
  
  // Helper function to handle links and line breaks in a comment part
  const renderCommentParts = (commentPart) => {
    if (!commentPart) return null;
    
    // First, handle line breaks
    const withLineBreaks = commentPart.split('\n').map((line, i, arr) => 
      i < arr.length - 1 ? (
        <React.Fragment key={`line-${i}`}>
          {line}
          <br />
        </React.Fragment>
      ) : line
    );
    
    // Then process links within each line
    return withLineBreaks.map((part, i) => {
      if (typeof part !== 'string') {
        // This is already a React element (line break)
        return part;
      }
      
      // Handle links
      return part.split(/{link:|}/g).map((subPart, j) => {
        if (subPart.includes(', url:')) {
          const [text, url] = subPart.split(', url:').map(s => s.trim());
          const isInternalLink = url.startsWith('#');
  
          return (
            <a 
              key={`link-${i}-${j}`}
              href={url}
              className="text-primary-purple hover:text-primary-orange underline"
              target={isInternalLink ? undefined : "_blank"}
              rel={isInternalLink ? undefined : "noopener noreferrer"}
            >
              {text}
            </a>
          );
        }
        return subPart;
      });
    });
  };

  const scheduleData = [
    {
      day: 'Friday (Public Holiday)',
      date: '25th April, 2025',
      events: [
        { 
          time: '1:30 pm', 
          title: 'Workshop: Repique Club', 
          location: 'Wizard Tone Studios',
          comments: 'Find registration link in the {link:Workshops section, url:#workshops}. {link:How to get there, url:https://maps.app.goo.gl/7XzfBVg8kVC7pKzY6}'
        },
        { 
          time: '1:30 pm', 
          title: 'Workshop: Bahia Samba Dance', 
          location: 'Cheltenham Community Centre',
          comments: 'Find registration link in the {link:Workshops section, url:#workshops}. {link:How to get there, url:https://maps.app.goo.gl/YcK48iezLagNGqfr7}'
        },
        { 
          time: '3:00 pm', 
          title: 'Workshop: The Drums of Estácio de Sá', 
          location: 'Wizard Tone Studios',
          comments: 'Find registration link in the {link:Workshops section, url:#workshops}. {link:How to get there, url:https://maps.app.goo.gl/7XzfBVg8kVC7pKzY6}'
        },
        { 
          time: '3:00 pm', 
          title: 'Workshop: Brazilian Funk', 
          location: 'Cheltenham Community Centre',
          comments: 'Find registration link in the {link:Workshops section, url:#workshops}. {link:How to get there, url:https://maps.app.goo.gl/YcK48iezLagNGqfr7}'
        },
        { 
          time: '4:30 pm', 
          title: 'Workshop: Bahia Samba Percussion', 
          location: 'Wizard Tone Studios',
          comments: 'Find registration link in the {link:Workshops section, url:#workshops}. {link:How to get there, url:https://maps.app.goo.gl/7XzfBVg8kVC7pKzY6}'
        },
        { 
          time: '4:30 pm', 
          title: 'Workshop: Queen of Drums: Understanding Musicality & Movement to the Bateria', 
          location: 'Cheltenham Community Centre',
          comments: 'Find registration link in the {link:Workshops section, url:#workshops}. {link:How to get there, url:https://maps.app.goo.gl/YcK48iezLagNGqfr7}'
        },
        { 
          time: '6:00 pm \'til late', 
          title: 'Welcome party/hangs/drinks', 
          location: 'Big Shed Brewing',
          comments: 'Open to the local and visiting Samba community. Absolutely not compulsory, but a chance to connect and hang with sambistas from far and wide. {link: How to get there, url: https://maps.app.goo.gl/neSWN6Sh6PLLWDtq7}'
        }
      ]
    },
    {
      day: 'Saturday',
      date: '26th April, 2025',
      events: [
        { 
          time: '10:00 am - 3:00 pm', 
          title: 'CACATUA General rehearsal', 
          location: 'The Flour Shed / HARTS Mill (9A Mundy St, Port Adelaide SA 5015)',
          comments: 'UNIDOS DA CACATUA members only. {link:Learn how to be become a member, url:https://docs.google.com/forms/d/10uCT_RYpleS6qodPjRNC--IsLPPZXiO_YFwrLNGr-9U/viewform?chromeless=1&edit_requested=true}. {link: How to get there, url: https://maps.app.goo.gl/8TAAGmAmbGRYyZoFA}' 
        },
        { 
          time: '6:00 pm', 
          title: 'Adelaide Encontro Showcase',
          location: 'The Flour Shed / HARTS Mill (9A Mundy St, Port Adelaide SA 5015)',
          comments: `SOLD OUT. NO TICKETS AT THE DOOR!! 
          
          Running order:
          -  6:00 pm: Doors open
          -  7:00 pm: Welcome to event
          -  7:05 pm: SaSamba (Adelaide)
          -  7:30 pm: Bloco 3K (Melbourne)
          -  7:55 pm: Bateria 61 (Sydney)
          -  8:20 pm: SambaFrog (Newcastle, NSW)
          -  8:45 pm: Bay Batucada (Hawke's Bay, New Zealand)
          -  9:05 pm: End of live performances and thank you from MC
          -  9:10 pm: DJ set
          - 11:00 pm: Event ends`
        }
      ]
    },
    {
      day: 'Sunday',
      date: '27th April, 2025',
      events: [
        { 
          time: '12:00 pm - 2:00 pm', 
          title: 'UNIDOS DA CACATUA Performance', 
          location: 'Moseley Square 1 Moseley Sq, Glenelg SA 5045',
          comments: 'FREE Performance. Open to all. We will perform outside, so be prepared for sun/wind/snow/hail/fun/party/whoop/yeah. {link: How to get there, url: https://maps.app.goo.gl/avyfB2QoDvHjykva8}'
        }
      ]
    }
  ];

  return (
    <section id="schedule" className="min-h-screen bg-gray-50 py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="space-y-8">
          {scheduleData.map((day, index) => (
            <Card key={index} className="overflow-hidden">
              <div className="bg-primary-purple text-white p-4 -mx-6 -mt-6 mb-6">
                <h3 className="text-2xl font-bold">{day.day}</h3>
                <p className="text-primary-yellow">{day.date}</p>
              </div>
              <div className="space-y-4">
                {day.events.map((event, eventIndex) => (
                  <div 
                    key={eventIndex}
                    className="grid grid-cols-1 md:grid-cols-12 gap-4 border-b last:border-b-0 pb-4 last:pb-0"
                  >
                    <div className="md:col-span-2 font-bold text-primary-orange">
                      {event.time}
                    </div>
                    <div className="md:col-span-4">
                      <h4 className="font-bold text-lg">{event.title}</h4>
                      <p className="text-gray-600">{event.location}</p>
                    </div>
                    <div className="md:col-span-6 text-gray-600 italic">
                      {renderComment(event.comments)}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Schedule;